import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { apiClient } from '@/lib/apiClient';

interface CommissionPayout {
  id: string;
  opportunityId: string;
  opportunityName: string;
  dealValue: number;
  ratePercent: number;
  payoutAmount: number;
  state: string;
  tierName: string | null;
  approvalNotes: string | null;
  createdAt: string;
}

export function MyCommissionPayoutsPage() {
  const { t } = useTranslation();
  const [payouts, setPayouts] = useState<CommissionPayout[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<CommissionPayout[]>('/account/commission-payouts');
      setPayouts(data);
    } catch {
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalPaid = payouts
    .filter((p) => p.state === 'PAID')
    .reduce((sum, p) => sum + Number(p.payoutAmount), 0);

  const totalApproved = payouts
    .filter((p) => p.state === 'APPROVED')
    .reduce((sum, p) => sum + Number(p.payoutAmount), 0);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Breadcrumbs crumbs={[{ label: t('commissionPayouts.title') }]} />
      <h1 className="text-2xl font-semibold mt-2">{t('commissionPayouts.title')}</h1>

      {!loading && payouts.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="border rounded-lg p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('commissionPayouts.filter_PAID')}</p>
            <p className="text-2xl font-semibold mt-1">{totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="border rounded-lg p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('commissionPayouts.filter_APPROVED')}</p>
            <p className="text-2xl font-semibold mt-1">{totalApproved.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="border rounded-lg p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('commissionPayouts.filter_PENDING')}</p>
            <p className="text-2xl font-semibold mt-1">
              {payouts.filter((p) => p.state === 'PENDING').reduce((sum, p) => sum + Number(p.payoutAmount), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground mt-6 text-sm">{t('common.loading')}</p>
      ) : payouts.length === 0 ? (
        <p className="text-muted-foreground mt-6 text-sm">{t('commissionPayouts.empty')}</p>
      ) : (
        <div className="mt-6 border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">{t('commissionPayouts.col_deal')}</th>
                <th className="text-right px-4 py-2">{t('commissionPayouts.col_payout')}</th>
                <th className="text-left px-4 py-2">{t('commissionPayouts.col_tier')}</th>
                <th className="text-left px-4 py-2">{t('commissionPayouts.col_state')}</th>
                <th className="text-left px-4 py-2">{t('commissionPayouts.col_date')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {payouts.map((p) => (
                <tr key={p.id} className="hover:bg-muted/20">
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.opportunityName}</div>
                    <div className="text-xs text-muted-foreground">
                      {Number(p.dealValue).toLocaleString(undefined, { minimumFractionDigits: 2 })} @ {Number(p.ratePercent).toFixed(1)}%
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">
                    {Number(p.payoutAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{p.tierName ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      p.state === 'PAID' ? 'bg-emerald-900/40 text-emerald-200' :
                      p.state === 'APPROVED' ? 'bg-blue-900/40 text-blue-200' :
                      'bg-amber-900/40 text-amber-200'
                    }`}>
                      {t(`commissionPayouts.status_${p.state}`)}
                    </span>
                    {p.approvalNotes && (
                      <p className="text-xs text-muted-foreground mt-0.5">{p.approvalNotes}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                    {new Date(p.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
